// @flow

import React from 'react';
import _ from 'lodash';

import { Document } from '@zapier/common-components';
import { pluralize } from '@zapier/common-utils';

import * as statuses from 'app/entity/utils/statuses';
import AsyncIndicator from 'app/common/components/AsyncIndicator';
import Button from 'app/common/components/Button';
import ButtonGroup from 'app/common/components/ButtonGroup';
import { attachAccountIdToUrl } from 'app/common/middlewares/accountSwitcherMiddleware';
import {
  InfoBlock,
  InfoBlockDescription,
  InfoBlockFooter,
  InfoBlockIcon,
  InfoBlockIconWrap,
  InfoBlockTitle,
} from 'app/common/components/InfoBlock';

const savingPhrases = [
  'Fun fact: Did you know Zapier rhymes with happier?',
  'By the way, we’re happy you’re here.',
  'Want to hum some hold music?',
  'Thanks for waiting—your Zaps are important to us.',
  'Why not take a quick stretch break?',
  'This shouldn’t take long, Zaps aren’t very heavy.',
  'Our Zapbots are working as quick as they can.',
];

const statusConfig = {
  [statuses.FAILURE]: {
    StatusActions: ({ onTryAgain }) => (
      <Button kind="primary xlarge" onClick={onTryAgain}>
        Try Again
      </Button>
    ),
    StatusDescription: ({ numZaps }) => (
      <p>
        Sorry, something went wrong while transferring your{' '}
        {pluralize('Zap', numZaps)}. Give it another try, or{' '}
        <a href="/app/contact-us">contact our support team</a>.
      </p>
    ),
    StatusIcon: () => <InfoBlockIcon kind="is-error" type="error" />,
    StatusTitle: () => <span>Transfer failed!</span>,
  },
  [statuses.SAVING]: {
    StatusDescription: ({ fromAccountName, numZaps, toAccountName }) => (
      <div>
        <p>
          We’re moving {numZaps} {pluralize('Zap', numZaps)} from your{' '}
          {fromAccountName} account to your {toAccountName} account. Kick back,
          we’ll be done in a jiffy!
        </p>
        <p>{_.sample(savingPhrases)}</p>
      </div>
    ),
    StatusIcon: () => <AsyncIndicator />,
    StatusTitle: () => <span>Transferring Zaps</span>,
  },
  [statuses.SUCCESS]: {
    StatusActions: props => (
      <ButtonGroup kindQueries={{ '(min-width: 400px)': 'horizontal' }}>
        <Button href="/app/settings" kind="xlarge secondary" tag="a">
          Go to Settings
        </Button>
        <Button
          href={attachAccountIdToUrl(props.toAccountId, '/app/zaps')}
          kind="primary xlarge"
          shouldRefreshPage={!props.didTransferToCurrentAccount}
          tag="Link"
        >
          View Moved Zaps
        </Button>
      </ButtonGroup>
    ),
    StatusDescription: ({ fromAccountName, numZaps, toAccountName }) => (
      <p>
        We moved {numZaps} {pluralize('Zap', numZaps)} from your{' '}
        {fromAccountName} account to your {toAccountName} account. To move more
        Zaps, <a href="/app/settings">visit the settings page.</a>
      </p>
    ),
    StatusIcon: () => <InfoBlockIcon kind="is-success" type="check" />,
    StatusTitle: () => <span>Transfer complete!</span>,
  },
};

type ZapMoverStatusProps = {
  didTransferToCurrentAccount: boolean,
  fromAccountId: number,
  fromAccountName: string,
  numZaps: number,
  status: statuses.FAILURE | statuses.SAVING | statuses.SUCCESS,
  toAccountId: number,
  toAccountName: string,
  onTryAgain: () => void,
};

const ZapMoverStatus = (props: ZapMoverStatusProps) => {
  const config = statusConfig[props.status] || statusConfig[statuses.FAILURE];
  const { StatusActions, StatusDescription, StatusIcon, StatusTitle } = config;
  const footer = !StatusActions ? null : (
    <InfoBlockFooter>
      <StatusActions {...props} />
    </InfoBlockFooter>
  );
  return (
    <Document title="Move Zaps: Status">
      <InfoBlock kind="page page-large">
        <InfoBlockIconWrap>
          <StatusIcon {...props} />
        </InfoBlockIconWrap>
        <InfoBlockTitle>
          <StatusTitle />
        </InfoBlockTitle>
        <InfoBlockDescription kind="centered">
          <StatusDescription {...props} />
        </InfoBlockDescription>
        {footer}
      </InfoBlock>
    </Document>
  );
};

export default ZapMoverStatus;
